import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { resolveEntityRoute } from '../../lib/mentions';
import { t } from '../../lib/i18n';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Bell,
  Calendar,
  Compass,
  Users,
  Book,
  CheckCheck,
  Bug,
  MessageSquare,
} from 'lucide-react';

const NOTIF_ICON = {
  session_published: Calendar,
  session_created: Calendar,
  quest_status_changed: Compass,
  npc_status_changed: Users,
  lore_revealed: Book,
  report_status_changed: Bug,
  report_admin_response: MessageSquare,
};

const NOTIF_I18N = {
  session_published: 'notif_session_published',
  session_created: 'notif_session_created',
  quest_status_changed: 'notif_quest_status_changed',
  npc_status_changed: 'notif_npc_status_changed',
  lore_revealed: 'notif_lore_revealed',
  report_status_changed: 'notif_report_status_changed',
  report_admin_response: 'notif_report_admin_response',
};

function timeAgo(dateStr, language) {
  const now = new Date();
  const date = new Date(dateStr + (dateStr.endsWith('Z') ? '' : 'Z'));
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return t('notif_just_now', language);
  if (diffMin < 60) return t('notif_minutes_ago', language).replace('{n}', diffMin);
  if (diffHr < 24) return t('notif_hours_ago', language).replace('{n}', diffHr);
  return t('notif_days_ago', language).replace('{n}', diffDay);
}

export const NotificationBell = () => {
  const { language } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get('/api/notifications/unread-count');
      setUnreadCount(res.data.count);
    } catch {
      // silent
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get('/api/notifications', { params: { limit: 30 } });
      setNotifications(res.data);
      setUnreadCount(res.data.filter(n => !n.is_read).length);
    } catch {
      // silent
    }
  }, []);

  // Poll unread count every 30 seconds
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Load full list when popover opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  const markAsRead = async (notif) => {
    if (notif.is_read) return;
    try {
      await axios.patch(`/api/notifications/${notif.id}/read`);
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // silent
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post('/api/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      // silent
    }
  };

  const handleClick = (notif) => {
    markAsRead(notif);
    if (notif.entity_type && notif.entity_id) {
      if (notif.entity_type === 'report') {
        // Report notifications don't navigate anywhere specific
        setOpen(false);
      } else if (notif.campaign_id) {
        const route = resolveEntityRoute(notif.campaign_id, notif.entity_type, notif.entity_id);
        if (route) {
          navigate(route);
          setOpen(false);
        }
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9" data-testid="notification-bell">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h4 className="text-sm font-semibold">{t('notif_title', language)}</h4>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="h-auto py-1 px-2 text-xs" onClick={markAllRead}>
              <CheckCheck className="mr-1 h-3 w-3" />
              {t('notif_mark_all_read', language)}
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-[360px]">
          {notifications.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {t('notif_empty', language)}
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notif) => {
                const Icon = NOTIF_ICON[notif.type] || Bell;
                const label = NOTIF_I18N[notif.type]
                  ? t(NOTIF_I18N[notif.type], language)
                  : notif.type;
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`w-full text-left px-4 py-3 hover:bg-accent/50 transition-colors flex gap-3 items-start ${
                      !notif.is_read ? 'bg-accent/20' : ''
                    }`}
                  >
                    <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground">{label}</p>
                      <p className={`text-sm truncate ${!notif.is_read ? 'font-medium' : ''}`}>
                        {notif.title}
                      </p>
                      {notif.body && (
                        <p className="text-xs text-muted-foreground truncate">{notif.body}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {timeAgo(notif.created_at, language)}
                      </p>
                    </div>
                    {!notif.is_read && (
                      <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1.5" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
